import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Mountain, Compass, MapPin, Calendar, Clock, TrendingUp, 
  ExternalLink, ChevronRight, Filter, Search, Loader2, Star
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DIFFICULTY_COLORS = {
  easy: { bg: 'bg-green-100', text: 'text-green-700', label: 'Easy' },
  easy_moderate: { bg: 'bg-lime-100', text: 'text-lime-700', label: 'Easy-Moderate' },
  moderate: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Moderate' },
  moderate_difficult: { bg: 'bg-orange-100', text: 'text-orange-700', label: 'Moderate-Difficult' },
  difficult: { bg: 'bg-red-100', text: 'text-red-700', label: 'Difficult' }
};

const TreksListing = () => {
  const navigate = useNavigate();
  const [treks, setTreks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    difficulty: '',
    state: '',
    season: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [sources, setSources] = useState([]);

  useEffect(() => {
    fetchTreks();
  }, []);

  const fetchTreks = async (appliedFilters = {}) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (appliedFilters.difficulty) params.set('difficulty', appliedFilters.difficulty);
      if (appliedFilters.state) params.set('state', appliedFilters.state);
      if (appliedFilters.season) params.set('season', appliedFilters.season);

      const response = await fetch(`${API_URL}/api/content/treks?${params.toString()}`);
      const data = await response.json();
      setTreks(data.treks || []);
      setSources(data.sources || []);
    } catch (error) {
      console.error('Failed to fetch treks:', error);
    }
    setLoading(false);
  };

  const handleSearch = () => {
    fetchTreks(filters);
  };

  const getDifficultyBadge = (difficulty) => {
    const style = DIFFICULTY_COLORS[difficulty] || DIFFICULTY_COLORS.moderate;
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${style.bg} ${style.text}`}>
        {style.label}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <Mountain className="h-8 w-8 text-[#2C5F8D]" />
              <span className="text-xl font-bold text-[#2C5F8D]" style={{fontFamily: 'Playfair Display, serif'}}>
                GoHimalayan
              </span>
            </Link>
            
            <nav className="hidden md:flex items-center gap-6">
              <Link to="/stays" className="text-gray-600 hover:text-gray-900">Stays</Link>
              <Link to="/treks" className="text-[#E67E22] font-medium">Treks</Link>
              <Link to="/events" className="text-gray-600 hover:text-gray-900">Events</Link>
            </nav>

            <div className="flex items-center gap-3">
              <Link to="/partner">
                <Button variant="outline" size="sm">Become a Host</Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero */}
      <div className="bg-gradient-to-r from-[#E67E22] to-[#D35400] text-white py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl md:text-4xl font-bold mb-3" style={{fontFamily: 'Playfair Display, serif'}}>
            Himalayan Treks & Hikes
          </h1>
          <p className="text-orange-100 max-w-2xl">
            Discover curated trekking adventures across the Himalayas. Content sourced from trusted trekking organizations.
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-wrap items-center gap-3">
            <select
              value={filters.difficulty}
              onChange={(e) => setFilters({...filters, difficulty: e.target.value})}
              className="px-3 py-2 border rounded-lg text-sm"
              data-testid="filter-difficulty"
            >
              <option value="">All Difficulties</option>
              <option value="easy">Easy</option>
              <option value="moderate">Moderate</option>
              <option value="difficult">Difficult</option>
            </select>

            <select
              value={filters.state}
              onChange={(e) => setFilters({...filters, state: e.target.value})}
              className="px-3 py-2 border rounded-lg text-sm"
              data-testid="filter-state"
            >
              <option value="">All Regions</option>
              <option value="Uttarakhand">Uttarakhand</option>
              <option value="Himachal">Himachal Pradesh</option>
              <option value="Sikkim">Sikkim</option>
              <option value="Nepal">Nepal</option>
              <option value="West Bengal">West Bengal</option>
            </select>

            <select
              value={filters.season}
              onChange={(e) => setFilters({...filters, season: e.target.value})}
              className="px-3 py-2 border rounded-lg text-sm"
              data-testid="filter-season"
            >
              <option value="">All Seasons</option>
              <option value="December">Winter (Dec-Feb)</option>
              <option value="March">Spring (Mar-May)</option>
              <option value="September">Autumn (Sep-Nov)</option>
            </select>

            <Button onClick={handleSearch} className="bg-[#E67E22] hover:bg-[#D35400]">
              <Search className="w-4 h-4 mr-2" />
              Search
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Disclaimer */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
          <p className="text-sm text-blue-700">
            <strong>Note:</strong> Content is curated from trusted trekking organizations. 
            For bookings and latest information, please visit the original source websites.
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#E67E22]" />
          </div>
        ) : treks.length === 0 ? (
          <div className="text-center py-20">
            <Compass className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No treks found. Try adjusting your filters.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {treks.map((trek) => (
              <div 
                key={trek.content_id} 
                className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all group"
                data-testid={`trek-card-${trek.content_id}`}
              >
                {/* Image */}
                <div className="relative h-48 bg-gray-100 overflow-hidden">
                  {trek.images && trek.images[0] ? (
                    <img 
                      src={trek.images[0]} 
                      alt={trek.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Compass className="w-12 h-12 text-gray-300" />
                    </div>
                  )}
                  <div className="absolute top-3 left-3">
                    {getDifficultyBadge(trek.difficulty)}
                  </div>
                </div>

                {/* Content */}
                <div className="p-4">
                  <h3 className="font-semibold text-lg text-gray-900 mb-1">{trek.title}</h3>
                  
                  <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
                    <MapPin className="w-4 h-4" />
                    <span>{trek.location?.state}</span>
                  </div>

                  <p className="text-sm text-gray-600 line-clamp-2 mb-3">{trek.description}</p>

                  {/* Stats */}
                  <div className="flex items-center gap-4 text-xs text-gray-500 mb-3">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {trek.duration_days} days
                    </span>
                    <span className="flex items-center gap-1">
                      <TrendingUp className="w-3 h-3" />
                      {trek.max_altitude}
                    </span>
                    <span>{trek.trek_distance}</span>
                  </div>

                  {/* Price */}
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-lg font-bold text-[#E67E22]">
                        ₹{trek.price_range?.min?.toLocaleString()}
                      </span>
                      <span className="text-sm text-gray-500"> - ₹{trek.price_range?.max?.toLocaleString()}</span>
                    </div>
                  </div>

                  {/* Source Link */}
                  <a 
                    href={trek.source?.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="mt-3 flex items-center justify-center gap-2 w-full py-2 border border-[#E67E22] text-[#E67E22] rounded-lg hover:bg-orange-50 transition-colors text-sm"
                  >
                    Book on {trek.source?.name}
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Sources */}
        <div className="mt-12 pt-8 border-t">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Content Sources</h3>
          <div className="flex flex-wrap gap-4">
            {sources.map((source, idx) => (
              <a 
                key={idx}
                href={source.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 bg-white border rounded-lg hover:bg-gray-50 text-sm"
              >
                <ExternalLink className="w-4 h-4 text-gray-400" />
                {source.name}
              </a>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TreksListing;
